import * as LodashFP from 'lodash/fp'

import { IStringAnyMap, IStringTMap } from '../../types/map'

export type ValidationMessage =
  | {
      status: 'ok'
    }
  | {
      status: 'warning'
      message: string
    }
  | {
      status: 'error'
      message: string
    }

export type FieldValidation = ValidationMessage[]

export interface IFormValidation {
  validUntil: number | 'end'
  fields: IStringTMap<FieldValidation>
}

export type FormValue = IStringAnyMap

export interface Option {
  value: string
  label: string
  disabled?: boolean
}

export interface SavedFile {
  url: string
  type: 'image' | 'video'
}

interface FormFieldBase {
  step: number
  name: string
  label: string
  description: string
  mandatory: boolean
  hidden?: boolean
  collapsable?: boolean
}

export interface FormFieldSelect extends FormFieldBase {
  type: 'select'

  meta: {
    as: 'dropdown' | 'grid' | 'checkboxes' | 'arrows' | 'radiobutton'
    multiple: boolean
    options: Option[]
    default?: any
  }
}

export interface FormFieldText extends FormFieldBase {
  type: 'text'
  meta?: {
    default?: string
    maximumCharacters: number
  }
}

export interface FormFieldBoolean extends FormFieldBase {
  type: 'boolean'
  meta?: {
    default?: boolean
  }
}

export interface FormFieldInteger extends FormFieldBase {
  type: 'integer'
  meta: {
    default: number
    min: number
    max: number
  }
}
export interface FormFieldFile extends FormFieldBase {
  type: 'file'
  meta: {
    maximumSize: number
    multiple: boolean
    allowedTypes: Array<string>
    fileType?: 'video' | 'image' | 'subtitle'
  }
}

export interface FormFieldList extends FormFieldBase {
  type: 'list'
  meta: {
    of: FormFieldBlueprint
    minimumElements?: number
    maximumElements?: number
  }
}

export interface FormFieldNestedForm extends FormFieldBase {
  type: 'fieldset'
  fields: FormFieldBlueprint[]
}

export type OptionMap = IStringTMap<Option[]>

export type NarrowOptions = (
  options: OptionMap,
  blueprint: FormBlueprint,
) => FormBlueprint

export type FileValue = null | File | SavedFile

export type FormFieldBlueprint =
  | FormFieldBoolean
  | FormFieldInteger
  | FormFieldSelect
  | FormFieldText
  | FormFieldFile
  | FormFieldList
  | FormFieldNestedForm

export type FormBlueprint = {
  name: string
  fields: FormFieldBlueprint[]
}

export function emptyFormGuidelines(): FormGuidelinesType {
  return {}
}

function emptyGuidelines(): Guidelines {
  return {
    unconditional: {},
    conditional: [],
  }
}

export function emptyFieldGuidelines(): FieldGuidelinesType {
  return {
    recommendations: emptyGuidelines(),
    requirements: emptyGuidelines(),
  }
}

export function areEmptyFieldGuidelines(guidelines: FieldGuidelinesType) {
  const empty = emptyFieldGuidelines()

  return LodashFP.isEqual(empty, guidelines)
}

export type ValueGuideline = {
  min: number | null
  max: number | null
  values?: number[]
}

export type Guidelines = {
  conditional: {
    conditions: GuidelineCondition[]
    guidelines: Guideline
  }[]
  unconditional: Guideline
}

export type Guideline = IStringTMap<ValueGuideline>

export type FieldGuidelinesType = {
  recommendations: Guidelines
  requirements: Guidelines
}

export type GuidelineCondition =
  | {
      type: 'empty'
      field: string
      complement: boolean
    }
  | {
      type: 'in'
      field: string
      complement: boolean
      values: any[]
    }
  | {
      type: 'between'
      field: string
      complement: boolean
      min: number
      max: number
    }
  | {
      type: 'min'
      field: string
      complement: boolean
      min: number
    }
  | {
      type: 'max'
      field: string
      complement: boolean
      max: number
    }

export type FormGuidelinesType = IStringTMap<FieldGuidelinesType>

const objectHasOnlyKeys = (obj: IStringAnyMap, wantedKeys: string[]) => {
  const actualKeys = Object.keys(obj)
  const sortedWanted = [...wantedKeys].sort()
  const sortedActual = [...actualKeys].sort()

  return LodashFP.isEqual(sortedActual, sortedWanted)
}

export type FormChangeHandler = (
  blueprint: FormBlueprint,
  value: FormValue,
) => Promise<{
  value: FormValue
  blueprint: FormBlueprint
  validation: IFormValidation
}>

export function isFormBlueprint(
  blueprint: FormBlueprint | FormFieldBlueprint,
): blueprint is FormBlueprint {
  return (
    objectHasOnlyKeys(blueprint, ['name', 'fields']) &&
    LodashFP.isString(blueprint.name) &&
    // TS is not smart enough to know that objectHasOnlyKeys call above rules
    // out any objects that don't have the fields property. I don't know how
    // that would be type-able. Hence, the ts-ignore below.

    // @ts-ignore
    LodashFP.isArray(blueprint.fields)
  )
}

export function isFormOrNestedFormBlueprint(
  blueprint: FormBlueprint | FormFieldBlueprint,
): blueprint is FormBlueprint | FormFieldNestedForm {
  if (isFormBlueprint(blueprint)) {
    return true
  } else {
    const b = blueprint as FormFieldBlueprint

    return b.type === 'fieldset'
  }
}

export function formValidation(): IFormValidation {
  return {
    validUntil: 'end',
    fields: {},
  }
}
